import { API } from "config/api";
import { API_ROUTES } from "config/apiRoutes";

export type FrameworkDomain = {
  code: string;
  name: string;
  description: string;
  sort_order: number;
  color_hex?: string;
};

export type FrameworkMilestone = {
  skill_level: number;
  name: string;
  description: string;
  epa_codes: string[];
  competency_codes: string[];
};

export type FrameworkCompetency = {
  code: string;
  name: string;
  description: string;
  domain_code: string;
  subcompetencies: string[];
};

export type FrameworkEpa = {
  code: string;
  name: string;
  description: string;
  commentary?: string;
  competency_codes: string[];
  primary_domain_codes: string[];
  secondary_domain_codes: string[];
};

export type FrameworkImportSource =
  | "cbve_parser"
  | "claude"
  | "claude_fallback";

export type CurriculumFrameworkImport = {
  source: FrameworkImportSource;
  framework_title?: string;
  domains: FrameworkDomain[];
  milestones: FrameworkMilestone[];
  competencies: FrameworkCompetency[];
  epas: FrameworkEpa[];
  expected_counts?: { domains: number; competencies: number };
  count_match?: boolean;
  import_blocked?: boolean;
  blocking_errors?: string[];
  fallback_reason?: string;
};

export type FrameworkReviewIssue = {
  severity: "error" | "warning" | "info";
  code: string;
  message: string;
  path?: string;
};

export type FrameworkReviewResult = {
  ok: boolean;
  summary: string;
  issues: FrameworkReviewIssue[];
  suggested_framework?: CurriculumFrameworkImport;
};

/**
 * Admin-only: convert a curriculum spreadsheet into structured JSON via the API.
 */
export async function convertRequirementsSpreadsheet(
  file: File,
  options?: { forceClaude?: boolean },
): Promise<CurriculumFrameworkImport> {
  const body = new FormData();
  body.append("file", file);
  if (options?.forceClaude) {
    body.append("forceClaude", "true");
  }

  const response = await API.post<CurriculumFrameworkImport>(
    API_ROUTES.AI.REQUIREMENTS_FROM_SPREADSHEET(),
    body,
    {
      headers: { "Content-Type": "multipart/form-data" },
    },
  );
  return response.data;
}

/**
 * Admin-only: Claude audit of extracted framework JSON vs the uploaded workbook.
 */
export async function reviewRequirementsSpreadsheet(
  file: File,
  framework: CurriculumFrameworkImport,
): Promise<FrameworkReviewResult> {
  const body = new FormData();
  body.append("file", file);
  body.append("framework", JSON.stringify(framework));

  const response = await API.post<FrameworkReviewResult>(
    API_ROUTES.AI.REQUIREMENTS_REVIEW_SPREADSHEET(),
    body,
    {
      headers: { "Content-Type": "multipart/form-data" },
    },
  );
  return response.data;
}
